import React, { useEffect, useRef } from 'react';

export default function OrbitalSystem({ 
  count = 150, 
  speedMultiplier = 1,
  particleColor = '#ffeedd', // Warm white/gold
  glowColor = '#ffaa44',     // Orange glow to match Neuralward aesthetic
  glitchKey = 0              // Bump to re-trigger the glitch animation
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Allow alpha for transparent background so the image beneath is visible
    const ctx = canvas.getContext('2d', { alpha: true }); 
    if (!ctx) return;

    let animationFrameId;
    let particles = [];
    let width = 0;
    let height = 0;

    const focalLength = 400;
    const containerRotation = 24 * (Math.PI / 180);

    const resize = () => {
      if (!canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.clientWidth;
      height = canvas.height = canvas.parentElement.clientHeight;
    };

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < count; i++) {
        const tilt = (Math.random() * 45) * (Math.PI / 180);
        particles.push({
          angle: Math.random() * Math.PI * 2,
          orbitSpeed: (0.003 + Math.random() * 0.007) * speedMultiplier,
          radius: 150 + Math.random() * 450,
          yOffset: Math.random() * 400 - 200,
          cosTilt: Math.cos(tilt),
          sinTilt: Math.sin(tilt),
          pulse: Math.random() * Math.PI * 2,
          pulseSpeed: 0.02 + Math.random() * 0.03,
          baseSize: 2 + Math.random() * 2
        });
      }
    };

    const render = () => {
      // Clear the canvas completely for transparency (NO background fill)
      ctx.clearRect(0, 0, width, height);

      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.rotate(containerRotation);

      const projected = particles
        .map(p => {
          p.angle += p.orbitSpeed * speedMultiplier;
          p.pulse += p.pulseSpeed;

          let x = Math.cos(p.angle) * p.radius;
          let z = Math.sin(p.angle) * p.radius;
          let y = p.yOffset;

          const y1 = y * p.cosTilt - z * p.sinTilt;
          const z1 = y * p.sinTilt + z * p.cosTilt;

          if (z1 <= -focalLength + 50) return null;

          const scale = focalLength / (focalLength + z1);
          
          // Cap maximum scale to prevent massive blobs
          const effectiveScale = Math.min(4, scale);
          const px = x * scale;
          const py = y1 * scale;
          const size = Math.max(0.1, p.baseSize * effectiveScale);
          
          // Depth of field fade: particles getting too close to the camera fade out
          let depthOpacity = Math.min(1, scale);
          if (scale > 2) {
            // Fade out smoothly as they approach the camera (scale 2 to 4)
            depthOpacity = Math.max(0, 1 - (scale - 2) / 2);
          }
          
          const opacity = (Math.sin(p.pulse) * 0.3 + 0.7) * depthOpacity;

          return { px, py, size, opacity, z: z1 };
        })
        .filter(p => p !== null && p.opacity > 0.01) // Don't render invisible particles
        .sort((a, b) => b.z - a.z);

      projected.forEach(p => {
        ctx.globalAlpha = Math.max(0, Math.min(1, p.opacity));
        // Only apply shadow glow for particles large enough to benefit
        if (p.size > 1.5) {
          ctx.shadowBlur = Math.min(12, p.size * 2); 
          ctx.shadowColor = glowColor;
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.fillStyle = particleColor;
        
        ctx.beginPath();
        ctx.arc(p.px, p.py, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();
      animationFrameId = requestAnimationFrame(render);
    };

    window.addEventListener('resize', resize);
    resize();
    initParticles();
    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [count, speedMultiplier, particleColor, glowColor]);

  // Re-trigger glitch animation when glitchKey changes
  useEffect(() => {
    if (glitchKey === 0) return; // Skip initial mount (already has the class)
    const el = canvasRef.current;
    if (!el) return;
    el.classList.remove('animate-vortex-glitch');
    void el.offsetWidth; // Force reflow
    el.classList.add('animate-vortex-glitch');
  }, [glitchKey]);

  return (
    <>
      <style>{`
        @keyframes vortexCyberGlitch {
          0% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          5% { transform: translate(-30px, 15px) scale(1.1) skewX(35deg); filter: drop-shadow(-15px 0 0 #ff003c) drop-shadow(15px 0 0 #00f0ff) brightness(2); opacity: 0.8; }
          10% { transform: translate(30px, -15px) scale(0.9) skewX(-35deg); filter: drop-shadow(15px 0 0 #ff003c) drop-shadow(-15px 0 0 #00f0ff) invert(0.8) contrast(2); opacity: 0.9; }
          15% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          25% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          30% { transform: translate(-15px, -5px) scale(1.05) skewX(15deg); filter: drop-shadow(-20px 5px 0 #fcee0a) drop-shadow(20px -5px 0 #00f0ff) brightness(3); opacity: 0.6; }
          35% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          60% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          65% { transform: translate(20px, 10px) scale(1) skewX(-20deg); filter: drop-shadow(25px 0 0 #ff003c) drop-shadow(-25px 0 0 #00f0ff) hue-rotate(90deg); opacity: 0.7; }
          70% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          85% { transform: translate(-5px, 5px) scale(1.02) skewX(5deg); filter: drop-shadow(-10px 0 0 #ff003c) drop-shadow(10px 0 0 #00f0ff); opacity: 0.9; }
          90% { transform: translate(0) scale(1); filter: none; opacity: 1; }
          100% { transform: translate(0) scale(1); filter: none; opacity: 1; }
        }
        .animate-vortex-glitch {
          animation: vortexCyberGlitch 0.45s step-end forwards;
        }
      `}</style>
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen animate-vortex-glitch"
      />
    </>
  );
}
